import React, { useReducer, useCallback, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Form, Segment, Button, Icon } from 'semantic-ui-react';
import { fetch } from 'services/http';
import { DateTime } from 'luxon';
import history from 'services/history';
import { getURLParameters } from 'utils';
import MaskedInput from 'react-text-mask';
import './styles.scss';

const initialValues = {
    unidade: '',
    periodo_ini: '',
    periodo_fim: '',
    tipo_recebivel: '',
    agrupar_por: 'unidade',
};

function reducer(state, action) {
    if (action.reset) {
        return initialValues;
    }
    return { ...state, [action.name]: action.payload };
}

const FilterComponent = forwardRef((props, ref) => {

    const [filter, setFilter] = useReducer(reducer, initialValues);
    const [companies, setCompanies] = useState([]);

    const onChange = useCallback((event, data) => {
        const name = data?.name || event.target.name;
        const value = data?.value || event.target.value;
        setFilter({ name, payload: value });
    }, []);

    useEffect(() => {
        fetch('/recebiveis/compliance-fiscal/companies')
            .then((response) => response.json())
            .then((data) => setCompanies(data));
    }, []);

    useImperativeHandle(ref, () => ({
        clear() {
            setFilter({ reset: true });
        }
    }));

    useEffect(() => {
        // ensure all params are loaded
        const search = history.location.search.trim();
        if (search !== '') {
            const params = getURLParameters(search);
            for (const param in params) {
                switch (param) {
                    case 'periodo':
                        const [dt1, dt2] = params[param].split(',').map((it) => DateTime.fromISO(it).toFormat('dd/MM/yyyy'));
                        setFilter({ name: 'periodo_ini', payload: dt1 });
                        setFilter({ name: 'periodo_fim', payload: dt2 });
                        break;

                    default:
                        setFilter({ name: param, payload: Number.isNaN(Number(params[param])) ? params[param] : Number(params[param]) });
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = useCallback(() => {
        const params = { periodo: [] };
        for (const key in filter) {
            switch (key) {
                case 'periodo_ini':
                    params.periodo[0] = DateTime.fromFormat(filter[key], 'dd/MM/yyyy').toISODate();
                    break;

                case 'periodo_fim':
                    params.periodo[1] = DateTime.fromFormat(filter[key], 'dd/MM/yyyy').toISODate();
                    break;

                default:
                    if (Boolean(filter[key])) {
                        params[key] = filter[key];
                    }
            }
        }

        if (props.onFilter) {
            props.onFilter(params);
        }
    }, [filter, props]);

    const clear = useCallback(() => {
        history.push({ search: null });
        setFilter({ reset: true });
    }, []);

    return (
        <Segment>
            <Form onSubmit={submit}>
                <Form.Group>
                    <Form.Select
                        style={{ width:250 }}
                        search
                        label="Unidade"
                        name="unidade"
                        value={filter.unidade}
                        onChange={onChange}
                        options={
                            [{ key: 0, value: 0, text: 'TODAS AS UNIDADES' }]
                                .concat(companies.sort((a, b) => Number(a.cod) - Number(b.cod)).map((it) => ({
                                    key: it.id,
                                    value: it.id,
                                    text: `${it.cod} - ${it.name}`,
                                })))
                        }
                    />
                    <Form.Field className="period-input-range" style={{ width: 300 }}>
                        <label htmlFor="periodo_ini">Período</label>
                        <MaskedInput
                            style={{ marginRight: 8 }}
                            className="masked-input"
                            mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                            name="periodo_ini"
                            showMask={true}
                            value={filter.periodo_ini}
                            onChange={onChange}
                        />
                        <MaskedInput
                            className="masked-input"
                            mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                            name="periodo_fim"
                            showMask={true}
                            value={filter.periodo_fim}
                            onChange={onChange}
                        />
                    </Form.Field>
                    <Form.Select
                        search
                        style={{ width: 150 }}
                        label="Tipo de Recebivel"
                        name="tipo_recebivel"
                        value={filter.tipo_recebivel}
                        onChange={onChange}
                        options={[
                            { key: 'nota_prazo', value: 'nota_prazo', text: 'Notas a prazo' },
                            { key: 'vale', value: 'vale', text: 'Vale a cliente' },
                            { key: 'pro_frota', value: 'pro_frota', text: 'Pró-Frotas' },
                            { key: 'ctf', value: 'ctf', text: 'CTF / CTF-Frete' },
                            { key: 'rodocred', value: 'rodocred', text: 'Rodocred' },
                            { key: 'cartao', value: 'cartao', text: 'Cartão' },
                        ]}
                    />
                    <Form.Select
                        label="Agrupar por"
                        name="agrupar_por"
                        value={filter.agrupar_por}
                        onChange={onChange}
                        options={[
                            { key: 'unidade', value: 'unidade', text: 'Unidade' },
                            { key: 'tipo_pagamento', value: 'tipo_pagamento', text: 'Tipo de Pagamento' },
                            { key: 'cliente', value: 'cliente', text: 'Cliente' },
                        ]}
                    />
                    <Form.Field style={{ width: 137, padding: 0 }}>
                        <label>&nbsp;</label>
                        <Button
                            primary
                            type="submit"
                            content={<><Icon name="search" /> Gerar</>}
                        />
                    </Form.Field>
                    <Form.Field style={{ width: 150, padding: 0 }}>
                        <label>&nbsp;</label>
                        <Button type="button" onClick={clear} content={<><Icon name="erase" />Limpar</>} />
                    </Form.Field>
                </Form.Group>
            </Form>
        </Segment>
    );
});
export const Filter = React.memo(FilterComponent);
